import { motion } from "motion/react";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useRef } from "react";
import type { Models } from "@appwrite.io/console";
import ansicolor, { type RGBValues } from "ansicolor";
import { useDeploymentLogs } from "@/hooks/use-deployment-logs";

const darkTheme: RGBValues = {
  black: [0, 0, 0],
  darkGray: [129, 129, 134],
  lightGray: [195, 195, 198],
  white: [255, 255, 255],
  red: [255, 69, 58],
  lightRed: [255, 69, 58],
  green: [16, 185, 129],
  lightGreen: [16, 185, 129],
  yellow: [254, 124, 67],
  lightYellow: [254, 124, 67],
  blue: [104, 163, 254],
  lightBlue: [104, 163, 254],
  magenta: [203, 194, 255],
  lightMagenta: [203, 194, 255],
  cyan: [133, 219, 216],
  lightCyan: [133, 219, 216],
};

const lightTheme: RGBValues = {
  black: [255, 255, 255],
  darkGray: [86, 86, 92],
  lightGray: [151, 151, 155],
  white: [0, 0, 0],
  red: [179, 18, 18],
  lightRed: [179, 18, 18],
  green: [10, 113, 79],
  lightGreen: [10, 113, 79],
  yellow: [97, 37, 10],
  lightYellow: [97, 37, 10],
  blue: [62, 98, 152],
  lightBlue: [62, 98, 152],
  magenta: [74, 62, 152],
  lightMagenta: [74, 62, 152],
  cyan: [78, 126, 124],
  lightCyan: [78, 126, 124],
};

function formatLogs(logs: string) {
  const parsed = ansicolor.parse(logs);
  return parsed.spans.map((span, idx) => {
    if (span.color?.name && span.css) {
      const colorMatch = span.css.match(/color:\s*([^;]+)/);
      const color = colorMatch?.[1]?.trim();
      return (
        <span key={idx} style={color ? { color } : undefined}>
          {span.text}
        </span>
      );
    }
    return <span key={idx}>{span.text}</span>;
  });
}

export function DeploymentLogs({
  deploymentId,
  initialLogs,
  onComplete,
  onLogsUpdate,
}: {
  deploymentId: string | null | undefined;
  initialLogs?: string;
  onComplete?: (deployment: Models.Deployment) => void;
  onLogsUpdate?: (logs: string) => void;
}) {
  const { logs } = useDeploymentLogs(
    deploymentId,
    initialLogs,
    onComplete,
    onLogsUpdate,
  );
  const { resolvedTheme } = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const hasScrolledOnceRef = useRef(false);

  // reset scroll
  useEffect(() => {
    shouldAutoScrollRef.current = true;
    hasScrolledOnceRef.current = false;
  }, [deploymentId]);

  ansicolor.rgb = resolvedTheme === "dark" ? darkTheme : lightTheme;

  const parsedLogs = useMemo(() => {
    if (!logs) return [];
    return logs.split("\n").filter((line) => line.trim().length > 0);
  }, [logs]);

  useEffect(() => {
    if (
      shouldAutoScrollRef.current &&
      scrollRef.current &&
      parsedLogs.length > 0
    ) {
      if (!hasScrolledOnceRef.current) {
        // instant jump
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        hasScrolledOnceRef.current = true;
      } else {
        // smooth scrolls
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  }, [parsedLogs]);

  const handleScroll = () => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    shouldAutoScrollRef.current =
      Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 180 }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
      className="overflow-hidden"
    >
      <div className="relative rounded-2xl overflow-hidden h-[180px] bg-muted border border-neutral-200 dark:border-neutral-700">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-full overflow-y-auto px-3 py-3"
          style={{
            fontFamily: "'Fira Code', 'Courier New', monospace",
            fontSize: "14px",
            lineHeight: "140%",
            fontWeight: 400,
            fontStyle: "normal",
          }}
        >
          {parsedLogs.length === 0 ? (
            <div className="text-muted-foreground text-sm">
              {!deploymentId
                ? "Waiting for deployment to start..."
                : "Waiting for deployment logs..."}
            </div>
          ) : (
            <div>
              {parsedLogs.map((line, idx) => (
                <div key={idx}>{formatLogs(line)}</div>
              ))}
            </div>
          )}
        </div>

        <div
          className="absolute inset-x-0 top-0 h-[51px] pointer-events-none z-10"
          style={{
            background:
              "linear-gradient(to bottom, var(--muted) 0%, transparent 100%)",
          }}
        />

        <div
          className="absolute inset-x-0 bottom-0 h-[51px] pointer-events-none z-10"
          style={{
            background:
              "linear-gradient(to top, var(--muted) 0%, transparent 100%)",
          }}
        />
      </div>
    </motion.div>
  );
}
